import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { brandBlogRecommendations } from '../../../../../shared/brand-blog/recommendations';

type BrandBlogArticlePageProps = {
  publisher: string;
  brandName: string;
  englishName: string;
};

function coverUrl(path: string) {
  return path.startsWith('/') ? `https://sonbook.net${path}` : path;
}

export default function BrandBlogArticlePage({
  publisher,
  brandName,
  englishName,
}: BrandBlogArticlePageProps) {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(
    () =>
      brandBlogRecommendations.find(
        (entry) => entry.publisher === publisher && entry.slug === slug,
      ),
    [publisher, slug],
  );
  const [articleReview, setArticleReview] = useState(article?.excerpt ?? '');

  useEffect(() => {
    let active = true;
    setArticleReview(article?.excerpt ?? '');

    if (!article) return () => { active = false; };

    import('../../../../../shared/brand-blog/article-reviews').then(({ brandBlogArticleReviews }) => {
      if (active) {
        setArticleReview(brandBlogArticleReviews[article.slug] ?? article.excerpt);
      }
    });

    return () => {
      active = false;
    };
  }, [article]);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content') ?? null;

    if (article) {
      document.title = `${article.title}｜${brandName}編輯選書文章`;
      description?.setAttribute(
        'content',
            `${articleReview.slice(0, 180)}｜${brandName}編輯選書文章`,
      );
    } else {
      document.title = `找不到文章｜${brandName}`;
    }

    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== null) {
        description.setAttribute('content', previousDescription);
      }
    };
  }, [article, articleReview, brandName]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 px-6 pb-24 pt-40 text-foreground-900">
        <div className="mx-auto max-w-3xl rounded-3xl border border-background-300 bg-background-100 p-10 text-center">
          <p className="text-xs font-label tracking-[0.22em] text-primary-600">{englishName} BLOG</p>
          <h1 className="mt-4 font-heading text-3xl font-semibold">找不到這篇文章</h1>
          <p className="mt-4 text-foreground-600">文章可能已下架，或網址尚未完成。</p>
          <Link
            to="/blog"
            className="mt-8 inline-flex rounded-full bg-primary-700 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
          >
            回到編輯選書文章
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50 text-foreground-900">
      <section className="bg-primary-950 pb-16 pt-32 text-background-50 md:pb-24 md:pt-40">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <div className="flex items-center gap-3 text-xs font-label tracking-[0.22em] text-background-200/70">
            <span>{englishName}</span>
            <span className="h-px w-10 bg-accent-400/70" />
            <span>EDITORIAL BLOG</span>
          </div>
          <div className="mt-8 max-w-4xl">
            <p className="text-sm text-accent-300">{article.category} · {article.publishDate}</p>
            <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight md:text-5xl">
              {article.title}
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-background-100/75 md:text-lg">
              {article.tagline}
            </p>
          </div>
        </div>
      </section>

      <article className="mx-auto grid max-w-6xl gap-10 px-6 py-12 md:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] md:px-10 md:py-20">
        <div className="self-start rounded-3xl border border-background-300/70 bg-white p-6 shadow-sm md:sticky md:top-28">
          <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-background-100 p-5">
            <img
              src={coverUrl(article.coverImage)}
              alt={article.title}
              className="h-full w-full object-contain"
            />
          </div>
          <div className="mt-5 flex items-center justify-between gap-4 text-sm text-foreground-500">
            <span>{article.publisher}</span>
            <span>ISBN {article.isbn}</span>
          </div>
        </div>

        <div className="max-w-3xl">
          <div className="mb-8 flex items-center justify-between gap-4 border-b border-background-300 pb-5">
            <div>
              <p className="text-xs font-label tracking-[0.2em] text-primary-600">EDITOR'S REVIEW</p>
              <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                編輯選書文章
              </h2>
            </div>
            <Link
              to="/blog"
              className="shrink-0 text-sm font-medium text-primary-700 transition-colors hover:text-primary-900"
            >
              返回文章列表
            </Link>
          </div>

          <div className="whitespace-pre-line text-base leading-[2] text-foreground-700 md:text-lg">
            {articleReview}
          </div>

          <div className="mt-12 rounded-2xl border border-primary-300/40 bg-primary-50/60 p-6">
            <p className="font-heading text-lg font-semibold text-primary-900">
              延伸閱讀
            </p>
            <p className="mt-2 text-sm leading-relaxed text-primary-800/75">
              本文與書籍資訊均保留在崧燁文化 SUNYA 官方網站，歡迎回到文章列表繼續閱讀其他編輯選書文章。
            </p>
            <Link
              to="/blog"
              className="mt-5 inline-flex rounded-full bg-primary-700 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              回到崧燁編輯選書文章
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
